/* ════════════════════════════════════════════════════════════
   SPA portfolio: WebGL light-rays background.
   Vanilla WebGL port of src/components/LightRays/LightRays.jsx
   (originally React + ogl). Same shader, same parameters.
   Auto-starts when #view-portfolio is visible, pauses when hidden.
   Exposes:
     window.__lightRaysStart() — start RAF loop, attach listeners
     window.__lightRaysStop()  — cancel RAF, detach listeners
   The router calls these in notifyLightRays() around portfolio
   view transitions. Inner IIFE preserves the original early-
   return semantics (3 bail-outs: missing DOM, no WebGL,
   shader compile fail).
   ════════════════════════════════════════════════════════════ */
'use strict';

(function () {
  'use strict';

  /* Parameters — middle-ground between the original (too strong)
     and the very-subtle pass. Visible as a backdrop, doesn't
     dominate the cover stack. */
  const CFG = {
    raysOrigin:     'top-center',
    raysColor:      '#4d6ccd',
    raysSpeed:      1.0,
    lightSpread:    1.2,
    rayLength:      1.0,
    pulsating:      false,
    fadeDistance:   1.05,
    saturation:     0.8,
    followMouse:    true,
    mouseInfluence: 0.11,
    noiseAmount:    0.05,
    distortion:     0.03,
  };
  /* Master opacity multiplier applied via canvas CSS (cheaper than
     altering the shader). 0.75 = visible but not overwhelming. */
  const CANVAS_OPACITY = 0.75;
  /* Duration of the CSS opacity fade applied around start()/stop().
     Lets the rays fade out as the user enters a case study and fade
     in as they return, instead of hard-cutting. */
  const FADE_MS = 350;

  function hexToRgb(hex) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return m
      ? [parseInt(m[1],16)/255, parseInt(m[2],16)/255, parseInt(m[3],16)/255]
      : [1, 1, 1];
  }
  function getAnchorAndDir(origin, w, h) {
    const o = 0.2;
    switch (origin) {
      case 'top-left':      return { anchor: [0,            -o*h], dir: [0, 1] };
      case 'top-right':     return { anchor: [w,            -o*h], dir: [0, 1] };
      case 'left':          return { anchor: [-o*w,        0.5*h], dir: [1, 0] };
      case 'right':         return { anchor: [(1+o)*w,     0.5*h], dir: [-1, 0] };
      case 'bottom-left':   return { anchor: [0,         (1+o)*h], dir: [0, -1] };
      case 'bottom-center': return { anchor: [0.5*w,     (1+o)*h], dir: [0, -1] };
      case 'bottom-right':  return { anchor: [w,         (1+o)*h], dir: [0, -1] };
      default:              return { anchor: [0.5*w,        -o*h], dir: [0, 1] };
    }
  }

  const VERT = '\n' +
    'attribute vec2 position;\n' +
    'varying vec2 vUv;\n' +
    'void main() {\n' +
    '  vUv = position * 0.5 + 0.5;\n' +
    '  gl_Position = vec4(position, 0.0, 1.0);\n' +
    '}';

  const FRAG = 'precision highp float;\n' +
    'uniform float iTime;\n' +
    'uniform vec2  iResolution;\n' +
    'uniform vec2  rayPos;\n' +
    'uniform vec2  rayDir;\n' +
    'uniform vec3  raysColor;\n' +
    'uniform float raysSpeed;\n' +
    'uniform float lightSpread;\n' +
    'uniform float rayLength;\n' +
    'uniform float pulsating;\n' +
    'uniform float fadeDistance;\n' +
    'uniform float saturation;\n' +
    'uniform vec2  mousePos;\n' +
    'uniform float mouseInfluence;\n' +
    'uniform float noiseAmount;\n' +
    'uniform float distortion;\n' +
    'varying vec2 vUv;\n' +
    'float noise(vec2 st) {\n' +
    '  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);\n' +
    '}\n' +
    'float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,\n' +
    '                  float seedA, float seedB, float speed) {\n' +
    '  vec2 sourceToCoord = coord - raySource;\n' +
    '  vec2 dirNorm = normalize(sourceToCoord);\n' +
    '  float cosAngle = dot(dirNorm, rayRefDirection);\n' +
    '  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;\n' +
    '  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));\n' +
    '  float distance = length(sourceToCoord);\n' +
    '  float maxDistance = iResolution.x * rayLength;\n' +
    '  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);\n' +
    '  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);\n' +
    '  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;\n' +
    '  float baseStrength = clamp(\n' +
    '    (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +\n' +
    '    (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)),\n' +
    '    0.0, 1.0\n' +
    '  );\n' +
    '  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;\n' +
    '}\n' +
    'void mainImage(out vec4 fragColor, in vec2 fragCoord) {\n' +
    '  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);\n' +
    '  vec2 finalRayDir = rayDir;\n' +
    '  if (mouseInfluence > 0.0) {\n' +
    '    vec2 mouseScreenPos = mousePos * iResolution.xy;\n' +
    '    vec2 mouseDirection = normalize(mouseScreenPos - rayPos);\n' +
    '    finalRayDir = normalize(mix(rayDir, mouseDirection, mouseInfluence));\n' +
    '  }\n' +
    '  vec4 rays1 = vec4(1.0) * rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349, 1.5 * raysSpeed);\n' +
    '  vec4 rays2 = vec4(1.0) * rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234,  1.1 * raysSpeed);\n' +
    '  fragColor = rays1 * 0.5 + rays2 * 0.4;\n' +
    '  if (noiseAmount > 0.0) {\n' +
    '    float n = noise(coord * 0.01 + iTime * 0.1);\n' +
    '    fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);\n' +
    '  }\n' +
    '  float brightness = 1.0 - (coord.y / iResolution.y);\n' +
    '  fragColor.x *= 0.1 + brightness * 0.8;\n' +
    '  fragColor.y *= 0.3 + brightness * 0.6;\n' +
    '  fragColor.z *= 0.5 + brightness * 0.5;\n' +
    '  if (saturation != 1.0) {\n' +
    '    float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));\n' +
    '    fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);\n' +
    '  }\n' +
    '  fragColor.rgb *= raysColor;\n' +
    '}\n' +
    'void main() {\n' +
    '  vec4 color;\n' +
    '  mainImage(color, gl_FragCoord.xy);\n' +
    '  gl_FragColor = color;\n' +
    '}';

  function compileShader(gl, src, type) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('LightRays shader compile error:', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh); return null;
    }
    return sh;
  }
  function makeProgram(gl) {
    const vs = compileShader(gl, VERT, gl.VERTEX_SHADER);
    const fs = compileShader(gl, FRAG, gl.FRAGMENT_SHADER);
    if (!vs || !fs) return null;
    const p = gl.createProgram();
    gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.warn('LightRays program link error:', gl.getProgramInfoLog(p));
      return null;
    }
    return p;
  }

  const canvas = document.getElementById('light-rays-canvas');
  const view   = document.getElementById('view-portfolio');
  if (!canvas || !view) return;
  canvas.style.opacity    = String(CANVAS_OPACITY);
  canvas.style.transition = 'opacity ' + FADE_MS + 'ms ease';

  /* Match ogl's Renderer defaults used by the original LightRays.jsx:
     alpha: true, antialias: true, premultipliedAlpha: true. */
  const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true })
          || canvas.getContext('experimental-webgl', { alpha: true, antialias: true, premultipliedAlpha: true });
  if (!gl) {
    console.warn('LightRays: WebGL not available'); return;
  }

  const program = makeProgram(gl);
  if (!program) return;

  /* Full-screen triangle (covers the viewport with 1 vertex draw) */
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1,  3, -1,  -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(program, 'position');

  /* Uniform locations */
  const U = {};
  [
    'iTime','iResolution','rayPos','rayDir','raysColor','raysSpeed',
    'lightSpread','rayLength','pulsating','fadeDistance','saturation',
    'mousePos','mouseInfluence','noiseAmount','distortion'
  ].forEach(name => { U[name] = gl.getUniformLocation(program, name); });

  /* Static uniform values */
  const raysColorRgb = hexToRgb(CFG.raysColor);
  let resW = 1, resH = 1;
  let rayPos = [0, 0], rayDir = [0, 1];

  function setStaticUniforms() {
    gl.uniform3f(U.raysColor,    raysColorRgb[0], raysColorRgb[1], raysColorRgb[2]);
    gl.uniform1f(U.raysSpeed,    CFG.raysSpeed);
    gl.uniform1f(U.lightSpread,  CFG.lightSpread);
    gl.uniform1f(U.rayLength,    CFG.rayLength);
    gl.uniform1f(U.pulsating,    CFG.pulsating ? 1.0 : 0.0);
    gl.uniform1f(U.fadeDistance, CFG.fadeDistance);
    gl.uniform1f(U.saturation,   CFG.saturation);
    gl.uniform1f(U.mouseInfluence, CFG.mouseInfluence);
    gl.uniform1f(U.noiseAmount,    CFG.noiseAmount);
    gl.uniform1f(U.distortion,     CFG.distortion);
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    /* Fall back to viewport size if the canvas hasn't been laid out
       yet (e.g. start() was called the same tick its parent had
       display:'' set, before browser layout). Without this fallback
       iResolution would stay [1,1] and the ray-length shader math
       would saturate the screen — visible as "too bright". */
    let wCSS = canvas.clientWidth;
    let hCSS = canvas.clientHeight;
    if (!wCSS || !hCSS) {
      wCSS = window.innerWidth;
      hCSS = window.innerHeight;
    }
    const w = Math.max(1, Math.round(wCSS * dpr));
    const h = Math.max(1, Math.round(hCSS * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w; canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    resW = w; resH = h;
    const ad = getAnchorAndDir(CFG.raysOrigin, w, h);
    rayPos = ad.anchor; rayDir = ad.dir;
  }

  /* Mouse tracking (in viewport-relative coords, smoothed in the loop) */
  const mouse  = { x: 0.5, y: 0.5 };
  const smooth = { x: 0.5, y: 0.5 };
  function onMouseMove(e) {
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    mouse.x = (e.clientX - r.left) / r.width;
    mouse.y = (e.clientY - r.top)  / r.height;
  }

  let raf = 0;
  let running = false;
  let startTime = 0;
  /* Pending teardown timer scheduled by stop(). While it's set,
     the fade-out is in flight and RAF is still running so the rays
     animate while fading. start() clears it to abort the teardown
     when the user toggles back to portfolio mid-fade. */
  let fadeoutTimer = 0;

  function frame(t) {
    if (!running) return;
    if (!startTime) startTime = t;
    const time = (t - startTime) * 0.001;

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.uniform1f(U.iTime, time);
    gl.uniform2f(U.iResolution, resW, resH);
    gl.uniform2f(U.rayPos, rayPos[0], rayPos[1]);
    gl.uniform2f(U.rayDir, rayDir[0], rayDir[1]);

    if (CFG.followMouse && CFG.mouseInfluence > 0) {
      const s = 0.92;
      smooth.x = smooth.x * s + mouse.x * (1 - s);
      smooth.y = smooth.y * s + mouse.y * (1 - s);
      gl.uniform2f(U.mousePos, smooth.x, smooth.y);
    } else {
      gl.uniform2f(U.mousePos, 0.5, 0.5);
    }

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    raf = requestAnimationFrame(frame);
  }

  function start() {
    /* Abort an in-flight fade-out: RAF is still running and
       listeners are still attached, we just need to restore the
       canvas opacity. */
    if (fadeoutTimer) { clearTimeout(fadeoutTimer); fadeoutTimer = 0; }

    if (running) {
      /* Already rendering — make sure we're visible (covers the
         mid-fade-out cancel above). */
      canvas.style.opacity = String(CANVAS_OPACITY);
      return;
    }
    running = true;
    startTime = 0;
    gl.useProgram(program);
    setStaticUniforms();
    resize();
    /* Re-resize on the next frame and once more shortly after, in
       case start() was called before the parent's layout settled. */
    requestAnimationFrame(resize);
    setTimeout(resize, 100);
    window.addEventListener('resize', resize);
    if (CFG.followMouse) window.addEventListener('mousemove', onMouseMove);

    /* Begin invisible, then fade in: two rAFs ensure the browser
       commits opacity:0 before we set the target value, otherwise
       the transition can be collapsed into an instant set. */
    canvas.style.opacity = '0';
    raf = requestAnimationFrame(frame);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        canvas.style.opacity = String(CANVAS_OPACITY);
      });
    });
  }
  function stop() {
    if (!running) return;
    /* Don't double-schedule a teardown if a fade-out is already
       in flight. */
    if (fadeoutTimer) return;

    /* Trigger CSS fade-out, then tear down RAF + listeners after
       the transition completes. RAF stays alive for the duration
       so the rays animate while fading instead of freezing. */
    canvas.style.opacity = '0';
    fadeoutTimer = setTimeout(function () {
      fadeoutTimer = 0;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      running = false;
    }, FADE_MS);
  }

  /* Route-driven start/stop. The router calls these explicitly
     on initial render and around the desktop↔portfolio transition. */
  window.__lightRaysStart = start;
  window.__lightRaysStop  = stop;
})();
